import { effect, inject, Injectable } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import ENV_NAMES from '../../../environments/ENV_NAMES';
import { environment } from '../../../environments/environment';
import { SessionStore } from '../../core/services/session.store';
import { APP_CONFIG } from '../injectionTokens';

import { EVENT_CATEGORIES, EVENT_INSTALLATION } from './analytics.service.constants';
import {
    AlertLogEvent,
    AlertsNotificationConfigLogEvent,
    AllowedEventsByCategory,
    TagCommanderContext,
    TriggerVars,
} from './analytics.service.types';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private analyticsInitialized = false;

    private readonly DEFAULT_TC_VARS: TagCommanderContext = {
        env_brand: 'SOMFY',
        env_channel: 'B2B',
        env_section: 'servego',
        env_work: undefined,
        env_currency: 'EUR',
    };

    private readonly translationService = inject(TranslateService);
    private readonly appConfig = inject(APP_CONFIG);
    private readonly session = inject(SessionStore);

    constructor() {
        window.tc_vars = { ...this.DEFAULT_TC_VARS };
        window.tc_vars.env_work = this.appConfig.name === ENV_NAMES.DEV ? 'test' : this.appConfig.name;

        this.translationService.onLangChange.subscribe((evt: LangChangeEvent) => {
            window.tc_vars = {
                ...window.tc_vars,
                env_language: evt.lang.toUpperCase(),
            };

            this.tryInitAnalytics();
        });

        effect(() => {
            const user = this.session.user();
            if (!user?.id) {
                return;
            }

            window.tc_vars = {
                ...window.tc_vars,
                user_id: user.id,
                env_country: user.country,
            };
            this.tryInitAnalytics();
        });

        this.tryInitAnalytics();
    }

    private tryInitAnalytics() {
        // Trigger container loading only once, when language is available in production.
        if (!environment.production || this.analyticsInitialized || !window.tc_vars.env_language) {
            return;
        }

        this.initAnalytics();
        this.analyticsInitialized = true;
    }

    private initAnalytics() {
        const path =
            this.appConfig.name === ENV_NAMES.PROD
                ? 'https://cdn.tagcommander.com/3887/'
                : 'https://cdn.tagcommander.com/3887/uat/';
        const files = ['tc_Somfy_21.js', 'tc_Somfy_34.js'];

        files.forEach((file) => {
            // Prevent duplicate script injection when initialization is retried.
            if (document.querySelector(`script[src="${path}${file}"]`)) {
                return;
            }

            const script = document.createElement('script');
            script.setAttribute('src', `${path}${file}`);
            document.body.appendChild(script);
        });
    }

    notifyNavigation(navigationEndEvent: NavigationEnd): Promise<void> {
        this.onPageChanged(navigationEndEvent);
        return this.reloadContainer();
    }

    private reloadContainer(): Promise<void> {
        return new Promise((resolve) => {
            if (window.tC) {
                window.tC.container.reload({ events: { all: [{}, {}] } });
                resolve();
            } else {
                // tc container not loaded yet. Wait a while
                setTimeout(() => {
                    if (window.tC) {
                        window.tC.container.reload({
                            events: { all: [{}, {}] },
                        });
                        resolve();
                    }
                }, 800);
            }
        });
    }

    private onPageChanged(navigationEndEvent: NavigationEnd) {
        const updatedVars: Partial<Record<'env_template' | 'page_name' | 'installation_id' | 'page_url', string>> = {};
        if (navigationEndEvent.url.startsWith('/sites')) {
            updatedVars['env_template'] = 'Sites';
            updatedVars['page_name'] = 'Sites - Servego';
        } else if (navigationEndEvent.url.startsWith('/alerts')) {
            updatedVars['env_template'] = 'Alerts';
            updatedVars['page_name'] = 'Alerts - Servego';
        } else if (navigationEndEvent.url.startsWith('/gateway/')) {
            updatedVars['env_template'] = 'Installation';
            updatedVars['page_name'] = 'Installation - Servego';
            const regexp = /gateway\/(\S+)/g;
            const res = regexp.exec(navigationEndEvent.url);
            updatedVars['installation_id'] = res?.[1];
        } else if (navigationEndEvent.url.startsWith('/notifications')) {
            updatedVars['env_template'] = 'Notifications';
            updatedVars['page_name'] = 'Notifications - Servego';
        }
        updatedVars['page_url'] = window.location.href.replace(/^http[s]?:\/\//, '');
        window.tc_vars = { ...window.tc_vars, ...updatedVars };
    }

    private triggerEvent<C extends keyof AllowedEventsByCategory>(
        category: C,
        event: AllowedEventsByCategory[C],
        vars: TriggerVars = {},
    ) {
        if (!('evt_button_action' in vars)) {
            vars.evt_button_action = event;
        }

        if (window.tc_events_global) {
            this.setGlobalEvent(category, event, vars);
        } else {
            // tc container not loaded yet. Wait a while
            setTimeout(() => {
                if (window.tc_events_global) {
                    this.setGlobalEvent(category, event, vars);
                }
            }, 800);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private setGlobalEvent(evt_category: string, id: string, vars: any) {
        try {
            window.tc_events_global(this, id, { evt_category, ...vars });
            console.log(`[Amplitude]`, { id, evt_category, ...vars });
        } catch (e) {
            console.error('Failed to emit analytic event', e);
        }
    }

    searchBoxFromInstallationScreen() {
        this.triggerEvent('installation', 'box_searched');
    }

    clickBoxThroughInstallationList() {
        this.triggerEvent('installation', 'box_list_click');
    }

    viewGatewayDetails() {
        this.triggerEvent(EVENT_CATEGORIES.INSTALLATION, EVENT_INSTALLATION.BOX_DETAILS);
    }

    viewCustomerDetails() {
        this.triggerEvent(EVENT_CATEGORIES.INSTALLATION, EVENT_INSTALLATION.CUSTOMER_DETAILS);
    }

    viewEventsHistory() {
        this.triggerEvent(EVENT_CATEGORIES.INSTALLATION, EVENT_INSTALLATION.EVENTS_HISTORY);
    }

    clickAlertsMenu() {
        this.triggerEvent('alerts', 'alerts_menu_click');
    }

    clickAlertConfig() {
        this.triggerEvent('alerts', 'alert_configuration_click');
    }

    logAlertsNotificationConfigChanges({ productType, name, isEnable }: AlertsNotificationConfigLogEvent) {
        const vars = { application_type: productType, alert_status: isEnable, name };
        this.triggerEvent('alerts', 'alert_managed', vars);
    }

    logAlertEvent(eventId: AllowedEventsByCategory['alerts'], alertEvent: AlertLogEvent) {
        this.triggerEvent('alerts', eventId, alertEvent);
    }

    logAlertSearched() {
        this.triggerEvent('alerts', 'alert_searched');
    }

    clickGlobalMenu() {
        this.triggerEvent('global_menu', 'global_menu_click');
    }

    accessGlobalMenuDocument(document_type: 'data privacy' | 'CGU' | 'legal notice' | 'assistance' | 'faq') {
        this.triggerEvent('global_menu', 'document_click', { document_type });
    }

    selectLanguage(language_selected: string) {
        const vars = { language_selected };
        this.triggerEvent('global_menu', 'language_modified_click', vars);
    }

    viewNotificationsMenu() {
        this.triggerEvent('notifications', 'menu_notifications_viewed');
    }

    resendLinkRequestFromNotification() {
        this.triggerEvent('notifications', 'link_request_resent_from_notification');
    }

    resendRemoteServiceRequestFromNotification() {
        this.triggerEvent('notifications', 'remote_service_request_resent_from_notification');
    }

    accessAlertsListFromNotification() {
        this.triggerEvent('notifications', 'alerts_list_access_from_notification');
    }

    viewMessageDetailFromNotification() {
        this.triggerEvent('notifications', 'message_detail_from_notification');
    }

    clickMapView() {
        this.triggerEvent('site_map', 'map_view_clicked');
    }

    viewSiteFromMapList() {
        this.triggerEvent('site_map', 'site_viewed_from_map_list');
    }

    shareData() {
        this.triggerEvent('data_sharing', 'data_shared');
    }

    addRemoteControl(control_mode: 'QR code' | 'prog') {
        const evt_button_action = 'add_remote_control_step1';
        const vars = { evt_button_action, control_mode };
        this.triggerEvent('controls_management', 'add_remote_control', vars);
    }

    selectedDeviceForRemoteControl(product_names: string[]) {
        const evt_button_action = 'add_remote_control_step2';
        const vars = {
            evt_button_action,
            evt_product_name: product_names.join(','),
        };
        this.triggerEvent('controls_management', 'add_remote_control', vars);
    }

    selectedRemoteControlType(control_type: string) {
        const evt_button_action = 'add_remote_control_step3';
        const vars = { evt_button_action, control_type };
        this.triggerEvent('controls_management', 'add_remote_control', vars);
    }

    selectInstallationTab(tab_selected: 'installation' | 'device' | 'service log') {
        const vars = { tab_selected };
        this.triggerEvent('installation', 'installation_tab_click', vars);
    }

    selectInstallationDevice(evt_product_type: string, evt_product_name: string) {
        const vars = { evt_product_type, evt_product_name };
        this.triggerEvent('installation', 'device_list_click', vars);
    }

    refreshDeviceSection(evt_product_name: string) {
        this.triggerEvent('installation', 'manual_refresh_click', {
            evt_product_name,
        });
    }

    boxLinkingRequested() {
        this.triggerEvent('box_linking', 'box_linking_requested');
    }

    boxServiceRequested() {
        this.triggerEvent('box_linking', 'box_service_requested');
    }

    boxDeleted() {
        this.triggerEvent('box_linking', 'box_deleted');
    }

    remoteServiceRequested(source: string) {
        this.triggerEvent('intervention', 'remote_service_requested', { source });
    }

    editDeviceParameter(evt_product_type: string, parameter_type: string, source: string) {
        const vars = { evt_product_type, parameter_type, source };
        this.triggerEvent('intervention', 'parameter_modified', vars);
    }

    interventionLinkClicked() {
        this.triggerEvent('intervention', 'remote_service_link_clicked');
    }

    interventionRequestClosed(comment: 'with comment' | 'without comment') {
        const vars = { comment };
        this.triggerEvent('intervention', 'intervention_request_ended', vars);
    }

    interventionAccepted() {
        this.triggerEvent('intervention', 'remote_service_accepted');
    }

    rebootBox() {
        this.triggerEvent('intervention', 'remote_box_reboot');
    }

    viewInterventionReport() {
        this.triggerEvent('intervention_history', 'view_report_click');
    }

    reportSentByEmail() {
        this.triggerEvent('intervention_history', 'email_report_sent');
    }

    logFrequencyChange(label: 'Daily' | 'Weekly') {
        this.triggerEvent('alert_emailing_frequency', label);
    }

    logAlertSubscriptionStatusChange(enabled: boolean) {
        this.triggerEvent('alert_emailing_status', enabled ? 'Enabled' : 'Disabled');
    }

    linkRequestAcceptLinkClicked() {
        this.triggerEvent('link_request', 'link_request_link_clicked');
    }

    linkRequestLoggedIn() {
        this.triggerEvent('link_request', 'link_request_logged_in');
    }

    linkRequestAccepted() {
        this.triggerEvent('link_request', 'link_request_accepted');
    }

    videoCallInvitationSent(source: string) {
        this.triggerEvent('visio_assistance', 'video_call_invitation_sent', { source });
    }

    videoCallJoined(source: string) {
        this.triggerEvent('visio_assistance', 'video_call_joined', { source });
    }

    videoCallStoppedAndJoined(source: string) {
        this.triggerEvent('visio_assistance', 'video_call_stopped_and_joined', { source });
    }
}
